import { CLASS_TYPES } from "./Const";

export const LESSON_TYPES_SETTING_ID = "lessonTypes";
export const LESSON_TYPE_CATEGORIES = ["G", "I", "D", "T", "O"];
export const LESSON_TYPE_STATUSES = ["ACTIVE", "INACTIVE"];

export const DEFAULT_LESSON_TYPES_SETTING = {
  id: LESSON_TYPES_SETTING_ID,
  types: CLASS_TYPES.map(type => ({
    classTypeId: type.id,
    category: type.id.charAt(0),
    name: type.name,
    durationMinutes: type.length,
    minStudents: type.minStudents,
    maxStudents: type.maxStudents,
    status: "ACTIVE",
  })),
};

export function generateClassTypeId(category, durationMinutes) {
  const normalizedCategory = String(category || "").trim().toUpperCase();
  const normalizedDuration = Number(durationMinutes);
  if (!LESSON_TYPE_CATEGORIES.includes(normalizedCategory)) throw new Error(`Unknown lesson type category: ${category}`);
  if (!Number.isInteger(normalizedDuration) || normalizedDuration <= 0) throw new Error("Lesson type duration must be a positive integer");
  return `${normalizedCategory}${normalizedDuration}`;
}

function normalizeStatus(type) {
  if (typeof type.status === "string") return type.status.trim().toUpperCase();
  return type.active === false ? "INACTIVE" : "ACTIVE";
}

function inferCategory(classTypeId, category) {
  const normalizedCategory = String(category || "").trim().toUpperCase();
  if (normalizedCategory) return normalizedCategory;
  return String(classTypeId || "").trim().charAt(0).toUpperCase();
}

function normalizePositiveInteger(value, fallback) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeLessonType(type = {}) {
  const classTypeId = String(type.classTypeId || type.id || "").trim().toUpperCase();
  const durationMinutes = normalizePositiveInteger(type.durationMinutes || type.length, null);
  const category = inferCategory(classTypeId, type.category);
  const status = normalizeStatus(type);
  const name = String(type.name || "").trim();
  const minStudents = normalizePositiveInteger(type.minStudents, 1);
  const maxStudents = normalizePositiveInteger(type.maxStudents, minStudents);

  if (!classTypeId) throw new Error("Lesson type code is required");
  if (!LESSON_TYPE_CATEGORIES.includes(category)) throw new Error(`Unknown lesson type category: ${category}`);
  if (!durationMinutes) throw new Error(`Invalid lesson type duration for ${classTypeId}`);
  if (classTypeId !== generateClassTypeId(category, durationMinutes)) {
    throw new Error(`Lesson type code must match category and duration for ${classTypeId}`);
  }
  if (!name) throw new Error(`Lesson type name is required for ${classTypeId}`);
  if (maxStudents < minStudents) throw new Error(`Invalid maxStudents for ${classTypeId}`);
  if (!LESSON_TYPE_STATUSES.includes(status)) throw new Error(`Invalid lesson type status for ${classTypeId}`);

  return { classTypeId, category, name, durationMinutes, minStudents, maxStudents, status };
}

export function normalizeLessonTypesSetting(setting = {}) {
  const hasExplicitTypes = Object.prototype.hasOwnProperty.call(setting, "types");
  if (hasExplicitTypes && (!Array.isArray(setting.types) || setting.types.length === 0)) {
    throw new Error("Lesson types must contain at least one type");
  }

  const sourceTypes = hasExplicitTypes ? setting.types : DEFAULT_LESSON_TYPES_SETTING.types;
  const seen = new Set();
  const types = sourceTypes.map(normalizeLessonType);
  types.forEach((type) => {
    if (seen.has(type.classTypeId)) throw new Error(`Duplicate lesson type code: ${type.classTypeId}`);
    seen.add(type.classTypeId);
  });

  return {
    ...setting,
    id: LESSON_TYPES_SETTING_ID,
    types,
  };
}

export function getLessonTypesSetting(settings = []) {
  const setting = settings.find(item => item.id === LESSON_TYPES_SETTING_ID);
  return normalizeLessonTypesSetting(setting || DEFAULT_LESSON_TYPES_SETTING);
}

export function getConfiguredLessonTypes(settings = [], selectedClassTypeId = "") {
  const setting = Array.isArray(settings) ? getLessonTypesSetting(settings) : normalizeLessonTypesSetting(settings);
  const selectedId = String(selectedClassTypeId || "").trim().toUpperCase();
  return setting.types.filter(type => type.status === "ACTIVE" || type.classTypeId === selectedId);
}

export function getLessonTypeById(setting, classTypeId) {
  const normalizedId = String(classTypeId || "").trim().toUpperCase();
  const normalizedSetting = normalizeLessonTypesSetting(setting);
  return normalizedSetting.types.find(type => type.classTypeId === normalizedId) || {
    classTypeId: normalizedId,
    category: inferCategory(normalizedId),
    name: normalizedId,
    durationMinutes: undefined,
    minStudents: undefined,
    maxStudents: undefined,
    status: "INACTIVE",
  };
}

export function getLessonTypeClassification(type = {}) {
  const category = inferCategory(type.classTypeId, type.category);
  return {
    category,
    isOnline: category === "O",
    isGroup: category === "G" || category === "T",
    isIndividual: category === "I" || category === "O",
    isPair: category === "D",
    countsAsGroupReport: category === "G" || category === "D" || category === "T",
  };
}

export function isGroupReportLessonType(classTypeId, lessonTypes = DEFAULT_LESSON_TYPES_SETTING) {
  return getLessonTypeClassification(getLessonTypeById(normalizeLessonTypesSetting(lessonTypes), classTypeId)).countsAsGroupReport;
}

export function getLessonTypeDisplayName(classTypeId, lessonTypes = DEFAULT_LESSON_TYPES_SETTING) {
  return getLessonTypeById(normalizeLessonTypesSetting(lessonTypes), classTypeId).name;
}

export function getSelectableLessonTypes(setting, selectedClassTypeId) {
  const normalizedSetting = normalizeLessonTypesSetting(setting);
  const selectedId = String(selectedClassTypeId || "").trim().toUpperCase();
  return normalizedSetting.types.filter(type => type.status === "ACTIVE" || type.classTypeId === selectedId);
}

export function toLegacyClassTypeOption(type) {
  return {
    ...type,
    id: type.classTypeId,
    length: type.durationMinutes,
    active: type.status === "ACTIVE",
  };
}

export function getConfiguredLessonTypeOptions(settings = [], selectedClassTypeId = "") {
  return getConfiguredLessonTypes(settings, selectedClassTypeId).map(toLegacyClassTypeOption);
}

export function getSelectableLessonTypeOptions(setting, selectedClassTypeId = "") {
  return getSelectableLessonTypes(setting, selectedClassTypeId).map(toLegacyClassTypeOption);
}
